import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  WithFetch,
  FetchData,
  WithTableLayout,
  TABLE_ACTION,
  TABLE_ACTION_STATE
} from '@qrios/frontend-library';

import { Dialog } from '../../../../../common/dialog';
import endpoints from '../../../../../../api/endpoints';
import { VoiceReportCreateForm } from '../reports/interfaces';
import TableFooterInfo from '../../../../../common/table/footerInfo';
import { PORTAL_PATH } from '../../../../../routes/paths';
import { billingTableEmptyMsg } from '../../../../../../constants/utils';
import { formatToAmount } from '../../../../../../constants/helpers/utils';
import GlobalLoaderOverlay from '../../../../../common/with-global-overlay';
import SearchByOperationId from '../../../../../common/search-by-operation-id';
import { handleEndDateFilter } from '../../../../../../constants/helpers/dates';
import { fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';
import { getQueryParams, handleRefreshOptions } from '../../../../../common/billing/apiUtils';
import { STEP } from '../../../../../common/search-by-operation-id/searchByOperationIdReducer';
import { useSearchByOperationId } from '../../../../../common/search-by-operation-id/useSearchByOperationId';

import locales from './locales';
import { columns } from './columns';
import FilterForm from './filterForm';
import Results from './operation-search-results';
import OperationDetails from './operation-details';
import { mapApiDataIntoTableFormat } from './dataTransformator';
import { activeFiltersCountMapper } from './filters';
import { VoiceBillingFilters, VoiceBillingResponse } from './interfaces';
import { useShowOperationDetails } from './operation-details/useShowOperationDetails';
import { FILTERS_PATH, FiltersContext } from '../../../../../common/filters/filtersContext';

const apiOptions = {
  totalCost: true
};

const Billing = () => {
  const history = useHistory();

  const { filtersValues, handleUpdateFilters } = useContext(FiltersContext);
  const [activeFilters, setActiveFilters] = useState(filtersValues.voice.billing);
  const initFetchOptions = getQueryParams({ filters: activeFilters, apiOptions });

  const {
    openDetailsDialog,
    handleOpenDetailsDialog,
    handleCloseDetailsDialog,
    selectedOperationDetails,
    waitingForDialogOpening
  } = useShowOperationDetails();

  const {
    searchByOperationIdState,
    handleSearchByOperationId,
    isSearching,
    openSearchOperationDialog,
    handleSearchOperationOpenDialog,
    handleSearchOperationCloseDialog
  } = useSearchByOperationId({ searchEndpoint: operationId => endpoints.voiceOperations(operationId) });

  const handleGenerateReport = () => {
    if (activeFilters) {
      const { from, to, operator, operation_id, app_id, msisdn, session_tag, session_close_reason } = activeFilters;
      const reportFilters: VoiceReportCreateForm = {
        name: '',
        from,
        to: handleEndDateFilter(to),
        operators: operator,
        operationIds: operation_id,
        msisdns: msisdn,
        voiceAppIds: app_id,
        sessionTags: session_tag,
        sessionCloseReasons: session_close_reason
      };
      history.push(PORTAL_PATH.VOICE_REPORTS, { filters: reportFilters });
    }
  };

  const tableRowActions = {
    openDetails: handleOpenDetailsDialog
  };

  return (
    <>
      <GlobalLoaderOverlay loading={waitingForDialogOpening} />
      <WithFetch
        fetchProvider={fetchWithTokenRefresh}
        pathname={endpoints.voiceBilling}
        options={initFetchOptions}
        withLoader={false}
        withError={false}
      >
        {({ data, refresh, loading, error, previousOptions }: FetchData<VoiceBillingResponse | undefined>) => (
          <WithTableLayout>
            <Table
              dataGridProps={{
                rows: !data?.items || loading ? [] : mapApiDataIntoTableFormat(data.items, tableRowActions),
                columns,
                loading,
                error: {
                  message: error,
                  refresh: () => refresh(previousOptions)
                },
                emptyMsg: billingTableEmptyMsg({
                  view: 'Voice',
                  operationType: 'sessions',
                  from: activeFilters.from,
                  to: handleEndDateFilter(activeFilters.to),
                  hasActiveFilters: activeFiltersCountMapper(activeFilters) > 0
                })
              }}
              footerProps={{
                tableInfo: (
                  <TableFooterInfo
                    label={locales.footer.label}
                    tooltip={locales.footer.tooltip}
                    value={data ? formatToAmount(data.totalCost.value) : '-'}
                  />
                ),
                reportProps: {
                  disabled: loading,
                  name: TABLE_ACTION.REPORT,
                  state: TABLE_ACTION_STATE.ACTIVE,
                  handler: handleGenerateReport
                },
                searchProps: {
                  disabled: loading,
                  name: TABLE_ACTION.SEARCH,
                  state: TABLE_ACTION_STATE.ACTIVE,
                  handler: handleSearchOperationOpenDialog
                },
                refreshProps: {
                  disabled: loading,
                  handler: () => {
                    refresh(handleRefreshOptions(previousOptions, activeFilters.to));
                  }
                },
                filtersProps: {
                  disabled: loading,
                  filters: activeFilters,
                  activeFiltersCount: activeFiltersCountMapper(activeFilters),
                  form: FilterForm,
                  handleSubmit: (updatedFilters: VoiceBillingFilters) => {
                    const queryParams = getQueryParams({ filters: updatedFilters, apiOptions });

                    handleUpdateFilters({
                      filtersToUpdatePath: FILTERS_PATH.VOICE_BILLING_PATH,
                      newFilters: updatedFilters
                    });
                    refresh(queryParams);
                    setActiveFilters(updatedFilters);
                  }
                },
                paginationProps: {
                  prev: {
                    disabled: loading || !data?.pagingContext.prevPageMarker,
                    handler: () => {
                      if (data?.pagingContext.prevPageMarker) {
                        const queryParams = getQueryParams({
                          filters: activeFilters,
                          apiOptions,
                          paginationOptions: { prev_marker: data.pagingContext.prevPageMarker }
                        });

                        refresh(queryParams);
                      }
                    }
                  },
                  next: {
                    disabled: loading || !data?.pagingContext.nextPageMarker,
                    handler: () => {
                      if (data?.pagingContext.nextPageMarker) {
                        const queryParams = getQueryParams({
                          filters: activeFilters,
                          apiOptions,
                          paginationOptions: { next_marker: data.pagingContext.nextPageMarker }
                        });

                        refresh(queryParams);
                      }
                    }
                  }
                }
              }}
            />
          </WithTableLayout>
        )}
      </WithFetch>
      <Dialog
        {...(searchByOperationIdState.currentPage === STEP.RESULTS && {
          height: '100%'
        })}
        open={openSearchOperationDialog}
        title={locales.dialog.search.title}
        handleClose={handleSearchOperationCloseDialog}
      >
        <SearchByOperationId
          detailsType="session"
          state={searchByOperationIdState}
          Results={Results}
          handleSearch={handleSearchByOperationId}
          isSearching={isSearching}
        />
      </Dialog>
      {selectedOperationDetails && (
        <Dialog handleClose={handleCloseDetailsDialog} open={openDetailsDialog} title={locales.dialog.details.title}>
          <OperationDetails operation={selectedOperationDetails} />
        </Dialog>
      )}
    </>
  );
};

export default Billing;
